import { ApiError } from '~/api/errors';

/**
 * Retry policy for experiment-detail analytics reads, which are backed by the
 * eventually-consistent `experiment_snapshot` projection in agentic-analytics.
 *
 * Split by failure kind, because the kinds want opposite handling:
 *
 * - **404 → retry up to 3×.** The snapshot existence-probe can transiently read
 *   a row that *does* exist as empty (a ClickHouse read-miss during a merge /
 *   replica catch-up / scaling event), so the route returns a FALSE 404. The
 *   global policy fails fast on every 4xx, which would pin a valid panel on
 *   "Data not available"; retrying lets the momentary miss self-heal. A
 *   genuinely-missing experiment still 404s on every retry and surfaces.
 * - **504 / 408 / client timeout → never retry.** These mean the analytics
 *   service or ClickHouse is already overloaded. Retrying re-runs the same
 *   heavy query on top of the one still running server-side — the 2026-10-08
 *   experimentation-tab incident turned one page view (11 calls) into up to 44
 *   this way. The panel shows a manual Retry instead.
 * - **502 / 503 / 429 / network → retry once**, with jittered backoff so a
 *   page's parallel calls don't retry in lockstep.
 * - **Everything else (other 4xx, 500) → fail fast.**
 *
 * Scope to snapshot-backed analytics reads only — never app-wide, where a 404
 * legitimately means "not found".
 */
const MAX_SNAPSHOT_MISS_RETRIES = 3;
const MAX_TRANSIENT_RETRIES = 1;

const TRANSIENT_STATUSES = new Set([429, 502, 503]);

/** Gateway timeout, request timeout, or Ky's own client-side timeout. */
export function isAnalyticsTimeout(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.statusCode === 504 || error.statusCode === 408;
  }
  // Ky's `TimeoutError` — matched by name because Ky is only imported in `~/api/client`.
  return error instanceof Error && error.name === 'TimeoutError';
}

export function retryExperimentSnapshotRead(failureCount: number, error: unknown): boolean {
  if (isAnalyticsTimeout(error)) return false;
  if (error instanceof ApiError && error.statusCode !== null) {
    if (error.statusCode === 404) return failureCount < MAX_SNAPSHOT_MISS_RETRIES;
    if (TRANSIENT_STATUSES.has(error.statusCode)) return failureCount < MAX_TRANSIENT_RETRIES;
    return false;
  }
  // A request aborted by Query itself (unmount / key change) is not a failure to retry.
  if (error instanceof Error && error.name === 'AbortError') return false;
  // Network failure (no response) or an ApiError without a status.
  return failureCount < MAX_TRANSIENT_RETRIES;
}

/**
 * Exponential backoff (1s, 2s, 4s… capped at 30s, Query's default curve) with
 * "equal jitter": half the delay is fixed, half random, so the ~11 parallel
 * reads on one experiment page don't all retry in the same instant.
 */
export function experimentSnapshotRetryDelay(failureCount: number): number {
  const base = Math.min(1000 * 2 ** failureCount, 30_000);
  return base / 2 + Math.random() * (base / 2);
}

/**
 * Query options shared by every experiment-detail analytics read (hooks and
 * prefetchers). Spread into `useQuery` / `prefetchQuery` so the policy lives in
 * one place. `refetchOnWindowFocus: false` because each refocus past staleTime
 * re-fired every heavy panel query at once; data refreshes on remount instead.
 */
export const EXPERIMENT_ANALYTICS_READ_OPTIONS = {
  retry: retryExperimentSnapshotRead,
  retryDelay: experimentSnapshotRetryDelay,
  refetchOnWindowFocus: false,
} as const;
